import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  ListItemText,
  Popper,
  TextField,
  listItemTextClasses,
  styled,
} from "@mui/material"
import type { FilterOptionsState } from "@mui/material"
import { Clear, Search } from "@mui/icons-material"
import {
  MatchableContributor,
  TableRowMatch,
  isCurrentLevelGrouped,
  matchTableRows,
  toTableRowMatches,
} from "../helpers/match-table-rows"
import { GO_TO_FINDER_MIN_WIDTH } from "../constants"

export const GROUPED_HINT_MESSAGE = "Clear grouping to find a specific security"
export const NO_MATCH_MESSAGE = "No matching securities"

// The panel tracks the ticker column's own width so it doesn't look mismatched when that column
// is wide, but needs a taller floor than the input's own min-width now that it shows a description too.
const RESULTS_PANEL_MIN_WIDTH = 300

const GoToFinderIconSlot = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: theme.spacing(0.5),
  top: "50%",
  transform: "translateY(-50%)",
  display: "flex",
  alignItems: "center",
}))

const GoToFinderInputSlot = styled(Box)(({ theme }) => ({
  position: "absolute",
  inset: 0,
  zIndex: 2,
  minWidth: GO_TO_FINDER_MIN_WIDTH,
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // theme.palette.* resolves to the frozen default-scheme value inside a styled() callback in
  // this app's cssVariables setup — theme.vars.palette.* is what actually emits the CSS var.
  backgroundColor: (theme.vars ?? theme).palette.background.paper,
}))

const ResultsPopper = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.tooltip,
  [`& .${listItemTextClasses.primary}, & .${listItemTextClasses.secondary}`]: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}))

const MATCH_SX = { display: "inline", color: "primary.main", fontWeight: "fontWeightBold" } as const

interface HighlightedTextProps {
  text: string
  query: string
}

function HighlightedText({ text, query }: HighlightedTextProps) {
  const trimmedQuery = query.trim()
  const matchStart = trimmedQuery ? text.toLowerCase().indexOf(trimmedQuery.toLowerCase()) : -1
  if (matchStart < 0) return text

  const matchEnd = matchStart + trimmedQuery.length
  return (
    <>
      {text.slice(0, matchStart)}
      <Box component="strong" sx={MATCH_SX}>{text.slice(matchStart, matchEnd)}</Box>
      {text.slice(matchEnd)}
    </>
  )
}

interface GoToFinderProps {
  rows: MatchableContributor[]
  onGoToRow: (match: TableRowMatch) => void
  headerCellNode: HTMLElement | null
  noMatchMessage?: string
}

function GoToFinder({ rows, onGoToRow, headerCellNode, noMatchMessage = NO_MATCH_MESSAGE }: GoToFinderProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [panelWidth, setPanelWidth] = useState<number | null>(null)
  const popperRef = useRef<HTMLDivElement | null>(null)

  const options = useMemo(() => toTableRowMatches(rows), [rows])
  const isGrouped = useMemo(() => isCurrentLevelGrouped(rows), [rows])

  // Toggled imperatively since this cell is MUI's own DOM node, not one we render ourselves.
  // The cleanup removes it so an unmount mid-search doesn't leave it stuck on that cell.
  useEffect(() => {
    headerCellNode?.classList.toggle("go-to-finder-searching", isOpen)
    return () => headerCellNode?.classList.remove("go-to-finder-searching")
  }, [headerCellNode, isOpen])

  // The table's own matcher stays the source of truth; Autocomplete only renders what it returns.
  const filterOptions = useCallback(
    (_: TableRowMatch[], state: FilterOptionsState<TableRowMatch>) => matchTableRows(rows, state.inputValue),
    [rows],
  )

  const close = useCallback(() => {
    setIsOpen(false)
    setInputValue("")
  }, [])

  // Autocomplete's own onClose only fires while its internal `open` is true, which is gated on
  // a non-empty query — so an outside click with an empty query would otherwise leave the search
  // box stuck open. This covers that case directly rather than depending on Autocomplete's state.
  useEffect(() => {
    if (!isOpen) return undefined

    function handleDocumentMouseDown(event: MouseEvent) {
      const target = event.target as Node
      const clickedInsideHeader = headerCellNode?.contains(target)
      const clickedInsidePopper = popperRef.current?.contains(target)
      if (!clickedInsideHeader && !clickedInsidePopper) close()
    }

    document.addEventListener("mousedown", handleDocumentMouseDown)
    return () => document.removeEventListener("mousedown", handleDocumentMouseDown)
  }, [isOpen, headerCellNode, close])

  const handleOpenSearch = useCallback((event: { stopPropagation: () => void }) => {
    event.stopPropagation()
    const cellWidth = headerCellNode?.getBoundingClientRect().width
    setPanelWidth(cellWidth ? Math.max(cellWidth, GO_TO_FINDER_MIN_WIDTH) : null)
    setIsOpen(true)
  }, [headerCellNode])

  if (!headerCellNode) return null

  if (!isOpen) {
    return createPortal(
      <GoToFinderIconSlot onClick={(event) => event.stopPropagation()}>
        <IconButton
          size="small"
          aria-label="Go to"
          onClick={handleOpenSearch}
          sx={{ color: "text.secondary" }}
          data-test-class="go-to-finder-icon-button"
        >
          <Search fontSize="inherit" />
        </IconButton>
      </GoToFinderIconSlot>,
      headerCellNode,
    )
  }

  return createPortal(
    <GoToFinderInputSlot
      sx={panelWidth ? { minWidth: panelWidth } : undefined}
      onClick={(event) => event.stopPropagation()}
      // DataGrid's own header keyboard nav (useGridKeyboardNavigation) only skips arrow/Tab/Enter/
      // Space keys when the event target is inside .MuiDataGrid-columnHeaderTitleContainerContent.
      // This slot portals into the header cell itself instead (to avoid title-container clipping),
      // so those keys would otherwise reach the grid and steal focus once Autocomplete's own
      // getRootProps().onKeyDown (bound below it, inside Autocomplete's root) has already handled them.
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Autocomplete<TableRowMatch, false, true, false>
        fullWidth
        autoHighlight
        disableClearable
        forcePopupIcon={false}
        openOnFocus
        blurOnSelect
        handleHomeEndKeys
        id="go-to-finder"
        options={options}
        filterOptions={filterOptions}
        open={isOpen && inputValue.trim().length > 0}
        onClose={close}
        inputValue={inputValue}
        onInputChange={(_, next) => setInputValue(next)}
        onChange={(_, match) => {
          if (match) onGoToRow(match)
          close()
        }}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        noOptionsText={isGrouped ? GROUPED_HINT_MESSAGE : noMatchMessage}
        slots={{ popper: ResultsPopper }}
        slotProps={{
          popper: {
            ref: popperRef,
            placement: "bottom-start",
            // Popper defaults to portaling into document.body, outside any locally-scoped
            // data-mui-color-scheme wrapper (e.g. a Storybook dark-mode preview) — anchoring
            // it inside the grid's own subtree instead keeps it in the same themed context.
            container: () => headerCellNode.closest(".contributors-datagrid") ?? headerCellNode,
            sx: { minWidth: RESULTS_PANEL_MIN_WIDTH },
          },
        }}
        renderOption={({ key, ...optionProps }, option, { inputValue: query }) => (
          <li key={key} {...optionProps}>
            <ListItemText
              primary={<HighlightedText text={option.label} query={query} />}
              secondary={option.description ? <HighlightedText text={option.description} query={query} /> : undefined}
            />
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            size="small"
            variant="outlined"
            placeholder="Go to…"
            // Covers Escape with an empty query, where Autocomplete's own popup is already
            // closed and its internal Escape handler no-ops — this is the only path that reaches here.
            onKeyDown={(event) => { if (event.key === "Escape") close() }}
            slotProps={{
              ...params.slotProps,
              htmlInput: { ...params.slotProps.htmlInput, "aria-label": "Go to" },
              input: {
                ...params.slotProps.input,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: inputValue.trim().length > 0 && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setInputValue("")}
                    >
                      <Clear fontSize="inherit" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
      />
    </GoToFinderInputSlot>,
    headerCellNode,
  )
}

export default GoToFinder
